import React, { useState, useRef, useEffect } from 'react';
import {
  X,
  Camera,
  Upload,
  User,
  Check,
  Trash2,
  AlertCircle,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { BodyProfile } from '../types';
import { api } from '../lib/api';
import { compressImage, fileToDataUrl, cropVideoToPortrait, rotateImageData90 } from '../lib/imageUtils';

interface BodyProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profiles: BodyProfile[];
  onProfileAdded: (profile: BodyProfile) => void;
  onProfileSetDefault: (id: string) => void;
  onProfileDeleted: (id: string) => void;
}

export const BodyProfileModal: React.FC<BodyProfileModalProps> = ({
  isOpen,
  onClose,
  profiles,
  onProfileAdded,
  onProfileSetDefault,
  onProfileDeleted,
}) => {
  if (!isOpen) return null;

  const [isAddingNew, setIsAddingNew] = useState<boolean>(profiles.length === 0);
  const [poseName, setPoseName] = useState<string>('Front Standing Pose');
  const [imageData, setImageData] = useState<string>('');
  const [heightCm, setHeightCm] = useState<string>('');
  const [bodyType, setBodyType] = useState<string>('Regular');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  // Camera state
  const [isCameraActive, setIsCameraActive] = useState<boolean>(false);
  const [cameraLoading, setCameraLoading] = useState<boolean>(false);
  const [cameraFacing, setCameraFacing] = useState<'environment' | 'user'>('user');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Start Camera with resilient fallback constraints
  const startCamera = async () => {
    stopCamera();
    setCameraLoading(true);
    setErrorMsg('');

    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { ideal: cameraFacing },
          aspectRatio: { ideal: 0.75 },
          width: { ideal: 1080 },
          height: { ideal: 1440 },
        },
        audio: false,
      };

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (fallbackErr) {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: cameraFacing },
            aspectRatio: { ideal: 0.75 },
          },
          audio: false,
        });
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playErr) {
          console.warn('Autoplay error:', playErr);
        }
      }

      setIsCameraActive(true);
    } catch (err: any) {
      console.warn('Camera error:', err);
      setErrorMsg('Camera access denied: ' + (err?.message || 'Check browser permissions.'));
      setIsCameraActive(false);
    } finally {
      setCameraLoading(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
  };

  const toggleCameraFacing = () => {
    setCameraFacing((prev) => (prev === 'user' ? 'environment' : 'user'));
  };

  // Keep video.srcObject synced when active
  useEffect(() => {
    if (isCameraActive && streamRef.current && videoRef.current) {
      if (videoRef.current.srcObject !== streamRef.current) {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.play().catch(console.warn);
      }
    }
  }, [isCameraActive, cameraFacing]);

  useEffect(() => {
    if (isCameraActive) {
      startCamera();
    }
  }, [cameraFacing]);

  useEffect(() => {
    if (!isOpen) {
      stopCamera();
    }
  }, [isOpen]);

  const takeSnapshot = async () => {
    if (!videoRef.current) return;
    try {
      // Force center-crop to 3:4 portrait, even if camera sensor is 16:9 landscape
      const raw = cropVideoToPortrait(videoRef.current, 3 / 4, 0.88);
      if (!raw) throw new Error('Could not capture frame');
      const compressed = await compressImage(raw, 1200, 0.82);
      setImageData(compressed);
      stopCamera();
    } catch (err) {
      setErrorMsg('Failed to process camera photo.');
    }
  };

  const handleRotatePhoto = async () => {
    if (!imageData) return;
    try {
      const rotated = await rotateImageData90(imageData);
      setImageData(rotated);
    } catch (err) {
      console.warn('Rotate failed:', err);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setErrorMsg('');

    try {
      const raw = await fileToDataUrl(file);
      const compressed = await compressImage(raw, 1200, 0.82);
      setImageData(compressed);
      stopCamera();
    } catch (err) {
      setErrorMsg('Failed to process uploaded body photo.');
    } finally {
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageData) {
      setErrorMsg('Please take or upload your full-body photo.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      const res = await api.profiles.create({
        name: poseName.trim() || 'Standing Pose',
        image_data: imageData,
        height_cm: heightCm ? parseInt(heightCm, 10) : undefined,
        body_type: bodyType,
        is_default: profiles.length === 0,
      });

      onProfileAdded(res.profile);
      setIsAddingNew(false);
      setImageData('');
      setPoseName('Front Standing Pose');
      stopCamera();
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to save body profile.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-md overflow-y-auto">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl my-auto animate-in fade-in zoom-in-95 duration-200">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div className="flex items-center space-x-2">
            <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
              <User className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">My Body Photos</h3>
              <p className="text-xs text-slate-400">Standing photos for virtual try-on previews</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {errorMsg && (
          <div className="mx-6 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2 text-xs text-red-300">
            <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
            <span>{errorMsg}</span>
          </div>
        )}

        <div className="p-6 space-y-6">
          {/* Profile Gallery / List */}
          {!isAddingNew && profiles.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Saved Poses ({profiles.length})
                </span>
                <button
                  type="button"
                  onClick={() => setIsAddingNew(true)}
                  className="inline-flex items-center space-x-1 text-xs font-bold text-brand-400 hover:text-brand-300"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add Another Pose</span>
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {profiles.map((p) => (
                  <div
                    key={p.id}
                    className={`relative rounded-2xl overflow-hidden border p-2 bg-slate-950 transition-all ${
                      p.is_default
                        ? 'border-brand-500 shadow-md shadow-brand-500/20'
                        : 'border-slate-800'
                    }`}
                  >
                    <div className="aspect-[3/4] bg-slate-900 rounded-xl overflow-hidden mb-2">
                      <img
                        src={p.image_data}
                        alt={p.name}
                        className="w-full h-full object-contain"
                      />
                    </div>

                    <h5 className="text-xs font-bold text-white truncate">{p.name}</h5>

                    <div className="mt-2 flex items-center justify-between">
                      {p.is_default ? (
                        <span className="text-[10px] font-bold text-emerald-400 flex items-center gap-0.5">
                          <Check className="w-3 h-3 stroke-[3]" />
                          <span>Active Pose</span>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onProfileSetDefault(p.id)}
                          className="text-[10px] text-brand-400 hover:text-brand-300 font-semibold"
                        >
                          Set Active
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => onProfileDeleted(p.id)}
                        className="text-slate-500 hover:text-red-400 p-1"
                        title="Delete photo"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Pose Form */}
          {isAddingNew && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="flex items-center justify-between pb-1">
                <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">
                  Upload Standing Photo
                </span>
                {profiles.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      stopCamera();
                      setIsAddingNew(false);
                    }}
                    className="text-xs text-slate-400 hover:text-white"
                  >
                    Cancel
                  </button>
                )}
              </div>

              {/* Photo viewfinder / preview */}
              <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 aspect-[3/4] max-w-xs mx-auto flex flex-col items-center justify-center shadow-inner">
                {/* Permanent Video Element */}
                <video
                  ref={videoRef}
                  playsInline
                  autoPlay
                  muted
                  // @ts-ignore
                  webkit-playsinline="true"
                  onLoadedMetadata={() => {
                    if (videoRef.current) {
                      videoRef.current.play().catch(console.warn);
                    }
                  }}
                  className={`w-full h-full object-cover ${
                    isCameraActive ? 'block' : 'hidden'
                  }`}
                />

                {/* Camera Live Controls & Portrait Guide */}
                {isCameraActive && (
                  <div className="absolute inset-0 flex flex-col justify-between p-3 pointer-events-none">
                    <div className="flex items-center justify-between pointer-events-auto">
                      <span className="px-2.5 py-1 rounded-full bg-slate-950/80 text-emerald-400 border border-emerald-500/30 text-[11px] font-mono font-bold">
                        PORTRAIT CAMERA
                      </span>

                      <button
                        type="button"
                        onClick={toggleCameraFacing}
                        className="p-2 rounded-full bg-slate-900/80 text-white backdrop-blur-md border border-white/20 active:scale-95"
                        title="Flip Camera"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Subtle Standing Body Outline Guide */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-30">
                      <svg viewBox="0 0 100 160" className="w-3/4 h-5/6 stroke-brand-400 stroke-[1.5] fill-none stroke-dasharray-[4,4]">
                        <circle cx="50" cy="22" r="12" />
                        <line x1="50" y1="34" x2="50" y2="40" />
                        <path d="M 28 46 Q 50 42 72 46 L 68 90 L 32 90 Z" />
                        <line x1="42" y1="90" x2="38" y2="148" />
                        <line x1="58" y1="90" x2="62" y2="148" />
                      </svg>
                    </div>

                    <div className="flex items-center justify-center gap-4 pointer-events-auto pb-1 z-10">
                      <button
                        type="button"
                        onClick={stopCamera}
                        className="p-3 rounded-full bg-slate-900/80 text-slate-300 hover:text-white border border-slate-700"
                        title="Cancel"
                      >
                        <X className="w-5 h-5" />
                      </button>

                      <button
                        type="button"
                        onClick={takeSnapshot}
                        className="w-14 h-14 rounded-full bg-white text-slate-950 flex items-center justify-center shadow-2xl ring-4 ring-brand-500/50 hover:scale-105 active:scale-95 transition-transform"
                        title="Snap Portrait Photo"
                      >
                        <div className="w-10 h-10 rounded-full border-2 border-slate-900 bg-white" />
                      </button>
                    </div>
                  </div>
                )}

                {cameraLoading && (
                  <div className="absolute inset-0 bg-slate-950/80 flex flex-col items-center justify-center gap-2 text-slate-300 z-10">
                    <RefreshCw className="w-7 h-7 text-brand-400 animate-spin" />
                    <span className="text-xs font-semibold">Opening camera feed...</span>
                  </div>
                )}

                {!isCameraActive && imageData && (
                  <div className="relative w-full h-full">
                    <img
                      src={imageData}
                      alt="Preview"
                      className="w-full h-full object-contain p-2"
                    />
                    <div className="absolute bottom-3 inset-x-3 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={handleRotatePhoto}
                        className="px-2.5 py-1.5 rounded-xl bg-slate-900/90 hover:bg-slate-800 text-white text-xs font-semibold shadow-md border border-slate-700 backdrop-blur-md transition-colors flex items-center gap-1.5"
                        title="Rotate photo 90 degrees"
                      >
                        <RefreshCw className="w-3.5 h-3.5 text-brand-400" />
                        <span>Rotate 90°</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setImageData('')}
                        className="px-3 py-1.5 rounded-xl bg-red-500/90 hover:bg-red-500 text-white text-xs font-semibold shadow-md backdrop-blur-md transition-colors"
                      >
                        Retake
                      </button>
                    </div>
                  </div>
                )}

                {!isCameraActive && !imageData && (
                  <div className="text-center p-6 space-y-3">
                    <div className="w-12 h-12 rounded-2xl bg-slate-900 flex items-center justify-center mx-auto text-slate-400 border border-slate-800">
                      <User className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-200">
                        Full-body standing photo
                      </p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Head to shoes clearly visible for try-on preview
                      </p>
                    </div>

                    <div className="flex items-center justify-center gap-2 pt-2">
                      <button
                        type="button"
                        onClick={startCamera}
                        className="inline-flex items-center space-x-1.5 px-3.5 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white text-xs font-bold shadow-md shadow-brand-600/30 active:scale-95"
                      >
                        <Camera className="w-4 h-4" />
                        <span>Camera</span>
                      </button>

                      {/* Native Label File Upload */}
                      <label className="inline-flex items-center space-x-1.5 px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-slate-200 text-xs font-bold border border-slate-700 cursor-pointer active:scale-95">
                        <Upload className="w-4 h-4 text-brand-400" />
                        <span>Upload</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Pose Name */}
              <div>
                <label className="block text-xs font-bold text-slate-300 uppercase tracking-wider mb-1">
                  Pose Description
                </label>
                <input
                  type="text"
                  placeholder="e.g. Front Standing Pose, Casual Mirror Selfie"
                  value={poseName}
                  onChange={(e) => setPoseName(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
                />
              </div>

              {/* Submit */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting || !imageData}
                  className="w-full py-2.5 rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white text-xs sm:text-sm font-bold shadow-lg shadow-brand-600/30 disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : 'Save Body Profile'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
